import { grantsCommunityMembershipEntitlement } from '../commerce/community-membership-billing';
import { memberTierMeetsMinimum, type TierAccessContext } from '../commerce/community-membership-tiers';
import type { CommunityMembershipContext } from '../policies/community-post.policy';
import type { CoursePerkModeValue } from './course-perk-pricing';

export const CourseStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
} as const;

export type CourseStatusValue = (typeof CourseStatus)[keyof typeof CourseStatus];

export const CourseAccessSource = {
  MEMBERSHIP_PERK: 'membership_perk',
  STANDALONE_PURCHASE: 'standalone_purchase',
} as const;

export type CourseAccessSourceValue = (typeof CourseAccessSource)[keyof typeof CourseAccessSource];

export type CourseSatelliteRef = {
  id: string;
  slug: string | null;
  title: string;
  description?: string | null;
  href: string;
  accessSource?: CourseAccessSourceValue | null;
  entitled?: boolean;
  perkMode?: CoursePerkModeValue | string;
  requiresPurchase?: boolean;
  checkoutAmountCents?: number | null;
  publicPriceAmountCents?: number | null;
  memberPriceAmountCents?: number | null;
  priceCurrency?: string | null;
  communitySlug?: string | null;
};

export type { CoursePerkModeValue };

export type CommunityClassroomTab = {
  visible: boolean;
  label: string;
  entitled: boolean;
  courses: CourseSatelliteRef[];
  message?: string | null;
};

export function isPublishedCourseStatus(status: string): boolean {
  return status === CourseStatus.PUBLISHED;
}

export function grantsCourseAccessViaMembershipPerk(
  membership: CommunityMembershipContext | null | undefined,
  accessType: string,
  perkEnabled: boolean,
  memberTier?: TierAccessContext,
  minTier?: TierAccessContext,
): boolean {
  if (!perkEnabled) return false;
  if (!grantsCommunityMembershipEntitlement(membership, accessType)) return false;
  return memberTierMeetsMinimum(memberTier, minTier);
}

export function buildCommunityClassroomTab(params: {
  courses: CourseSatelliteRef[];
  membershipEntitled: boolean;
  satelliteEnabled: boolean;
}): CommunityClassroomTab {
  const visible = params.satelliteEnabled && params.courses.length > 0;
  return {
    visible,
    label: 'Classroom',
    entitled: params.membershipEntitled,
    courses: params.courses.map((course) => ({
      ...course,
      entitled: course.entitled ?? false,
      accessSource: course.accessSource ?? null,
    })),
    message: visible
      ? params.membershipEntitled
        ? null
        : 'Join the community to unlock classroom courses.'
      : null,
  };
}

export function slugifyCourseTitle(title: string): string {
  const base = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return base || 'course';
}
